import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Translate } from 'react-localize-redux'
import isEqual from 'fast-deep-equal'
import { formatDate, getEntityStatus, translate } from 'shared/helpers/utils'
import { fetchProfile } from 'core/actions/Profile.actions'
import { withPermissionsHOC } from 'core/hoc/permissionsHoc'
import {
  LIMIT_10,
  PAGE_LEVEL_ERROR_NOTIFICATION,
  PAGE_LEVEL_WARN_NOTIFICATION,
  RouterConfig
} from 'core/utils/constants'
import { IAppState } from 'core/store/store'
import { IFilters, ISort } from 'core/actions/Actions.interfaces'
import { BasePathConst } from 'configs/Path.configs'
import { IAccessScope, ITenant } from 'reducers/Reducers.interfaces'
import { IStateProps, PropsType } from './Tenants.types'
import { deleteTenants, fetchExpiringAccounts, fetchTenants } from './Tenants.actions'
import history from 'core/history/history'
import {
  Alert,
  AlertTypes,
  Button,
  FreeTextFilter, SecondaryNav,
  SelectFilter, SideRail,
  Table,
  TableColumn,
  TableMetaProps,
  Tag,
  TagType
} from '@digicert/dcone-common-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/pro-light-svg-icons'
import styles from './tenants.page.module.scss'
import moment from 'moment'
import { getItemFromLS, setItemToLS } from 'shared/helpers/LocalStorage'
import { resizeTableBody } from '../../shared/helpers/Table'
import { Link } from 'react-router-dom'
import CreateAccount from '../create-account/CreateAccount';
import { getApplications } from 'secure-pages/settings/Settings.actions'
import cn from "classnames";
import EditOverView from './tenant/overview/edit/EditOverView'
import {IconProp} from "@fortawesome/fontawesome-svg-core";

const TenantsPage = (props: PropsType): ReactElement => {
  const {accessScope, fetchExpiringAccounts, profile, tenants, permissions, localeList, getApplications} = props
  const defaultFilters: any = {
    active: [
      {
        value: 'true',
        func: "eq",
        label: translate('common.statuses.active') as string
      }
    ]
  }

  let metaOnMount: TableMetaProps<ITenant> = {
    pageSize: LIMIT_10,
    currentPage: 1,
    totalItems: 0,
    filters: defaultFilters
  };
  const tableMeta = JSON.parse(getItemFromLS('dcone-table/accounts-table-' + profile?.id) as string)
  if (tableMeta !== null && tableMeta.meta.filters !== undefined && Object.entries(tableMeta.meta.filters).length !== 0) {
    metaOnMount = {
      pageSize: LIMIT_10,
      currentPage: 1,
      totalItems: 0,
      filters: tableMeta.meta.filters
    };
  }

  const [current, setCurrent] = useState(1)
  const [sortedInfo, setSortedInfo] = useState<any>({
    columnKey: 'name',
    order: 'ascend'
  })
  const [tenantIds, setTenantIds] = useState<string[]>([])
  const [meta, setMeta] = useState(metaOnMount)
  const [showPageLevelWarning, setShowPageLevelWarning] = useState(false)
  const [showPageLevelError, setShowPageLevelError] = useState(false)
  const [warningMessage, setWarningMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [showErrorBannerAfterDismiss, setShowErrorBannerAfterDismiss] = useState(true)
  const [showWarnBannerAfterDismiss, setShowWarnBannerAfterDismiss] = useState(true)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<any>()
  const [showSideRail, setShowSideRail] = useState(false)
  const [showEditSideRail, setShowEditSideRail] = useState(false)
  const [applications, setApplications] = useState([])
  const [account, setAccount] = useState<ITenant>()

  const onFilter = (filters?: IFilters) => {
    setFilters(filters)
  }

  const parseFilter = (newMeta): any => {
    const tagTypes: any = ['active', 'inactive']
    // format array into structure compatible with onFilter
    if(newMeta.filters){
      const tmpArray: any = Object.getOwnPropertyNames(newMeta.filters).map((key: string) => {
        if (newMeta.filters) {
          const item = newMeta.filters[key]
          if (item) {
            // format end date to work with API
            if (key == 'endDate') {
              if (item[0].value.option == 'days_from_current_date') {
                return {
                  [key]: [moment.utc().format('YYYY-MM-DD').toString(),  moment.utc().add(item[0].value.value, 'days').format('YYYY-MM-DD').toString()]
                }
              }
              if (item[0].value.option == 'date_range') {
                item[0].value.value.map((option) => {
                  if(option.value) {
                    return moment(option.value).format('YYYY-MM-DD').toString()
                  }
                })
                return {
                  [key]: [item[0].value.value.map(val => moment(val).format('YYYY-MM-DD').toString())]
                }
              }
            }
            // format tagType/status, API expects active of boolean type where table filter expects string
            if (tagTypes.includes(key)) {
              if (newMeta.filters?.active) {
                const values = new Set(
                    newMeta.filters.active.map((option) => option.value)
                );
                if (values.size >= 2) {
                  return {}
                }
              }
              return {
                [key]: item[0].value == 'true'
              }
            }

            if (key === 'applications') {
              return {
                [key]:item.map(val => {
                  return val.value
                })
              }
            }
            else {
                return {
                    [key]: item[0].value
              }
            }
          }
        }
      });
      // comma separate remaining properties from array
      const filter = Object.assign({}, ...tmpArray)
      onFilter(filter)
      return filter

    }

    // clear filters
    else {
      onFilter(Object.assign({}, {}))
    }
  }

  const fetchTenants = useCallback((metaProps?: TableMetaProps<ITenant>) => {
    const params: ISort = { limit: metaProps ? metaProps.pageSize : 10, offset: metaProps ? metaProps.pageSize * (metaProps.currentPage - 1) : 0 }
    if (sortedInfo.order && sortedInfo.columnKey) {
      params.sort = [{
        columnKey: `${sortedInfo.columnKey}:${sortedInfo.order === 'descend' ? 'desc' : 'asc'}`
      }]
    }
    props.fetchTenants(params, metaProps ? parseFilter(metaProps) : filters).then((response: any) => {
      if (response && metaProps) {
        setMeta({ ...metaProps, ...{totalItems: response.total} });
        setLoading(false)
      }
    })
  }, [filters])

  const onSelectionChange = (tenantIds: string[] | number[]): void => {
    setTenantIds(tenantIds as string[])
  }

  useEffect(() => {
    if (getItemFromLS(PAGE_LEVEL_ERROR_NOTIFICATION) != null) {
      setShowErrorBannerAfterDismiss(false)
    }
    if (getItemFromLS(PAGE_LEVEL_WARN_NOTIFICATION) != null) {
      setShowWarnBannerAfterDismiss(false)
    }
    if(accessScope === IAccessScope.system) {
      fetchExpiringAccounts().then((response) => {
        const numberOfAccountsExpiring = new Map(Object.entries(response));
        if (numberOfAccountsExpiring.get('30')) {
          setErrorMessage( <Translate id={'common.form.accountExpiryNotification.expiringInDays'} data={{ amount: numberOfAccountsExpiring.get('30') as string, day: '30' }} /> as unknown as string)
          setShowPageLevelError(true)
        }
        if (numberOfAccountsExpiring.get('60')) {
          setWarningMessage(<Translate id={'common.form.accountExpiryNotification.expiringInDays'} data={{ amount: numberOfAccountsExpiring.get('60') as string, day: '60' }} /> as unknown as string)
          setShowPageLevelWarning(true)
        }
      })
    }
    const tableElement = document.getElementsByClassName('resizable-table-body')[0] as HTMLElement;
    if(tableElement) {
      resizeTableBody(tableElement)
      window.addEventListener('resize', () => resizeTableBody(tableElement))
    }
  }, [])

  useEffect(() => {
    if(accessScope == IAccessScope.account && tenants.length === 1) {
      history.push(`${BasePathConst}/accounts/${tenants[0].id}`)
    }
  }, [tenants]);

  useEffect(() => {
    if (getApplications) {
      getApplications(true).then((response) => {
        setApplications(response.flat())
      })
    }
  }, []);

  const populateTable = (day: number) => {
    const filter: TableMetaProps<ITenant> = {
      pageSize: LIMIT_10,
      currentPage: 1,
      totalItems: 0,
      filters: {
        active: [
          {
            value: 'true',
            func: "eq",
            label: translate('common.statuses.active') as string
          }
        ],
        endDate: [
          {
            value: {
              option: "days_from_current_date",
              type: "numberInput",
              value: day
            },
            func: "eq",
            label: day.toString() + translate('common.table.filters.daysFrom') as string
          }
        ]
      }
    };
    setMeta(filter)
    parseFilter(filter)
    fetchTenants(filter)
  }

  const  onDismissErrorBanner = () => {
    setShowErrorBannerAfterDismiss(false)
    setItemToLS(PAGE_LEVEL_ERROR_NOTIFICATION, 'false')
  }

  const onDismissWarnBanner = () => {
    setShowWarnBannerAfterDismiss(false)
    setItemToLS(PAGE_LEVEL_WARN_NOTIFICATION, 'false')
  }

  const applicationOptions = (): any =>  {
    const applications: Array<any> = []
    if (profile && profile?.applications) {
      Object.keys(profile?.applications).reduce(
          (val, key) => {
            const application = {
              label: profile?.applications[key].name,
              value: profile?.applications[key].id,
              func: 'eq'
            }
            applications.push(application)
            return application
          },
          {}
      )
    }
    applications?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return applications
  }

  const renderAppNames = (account) => {
    let appString = ''
    account.applications.map((app, index) => {
      if (index === account.applications.length -1) {
        appString += app.name
      } else {
        appString += app.name + ", "
      }
    })
    return (
        <>
          {appString}
        </>
    )
  }

  const getColumns = (): TableColumn<ITenant>[] => [
    {
      key: 'name',
      displayRowActions: true,
      title: translate('common.form.name') as string,
      sorter: true,
      renderFilter: function BasicStringFilter() {
        return (
            <FreeTextFilter<ITenant>
                label='Name'
                columnKey='name'
            />
        );
      },
      render: tenant => {
        if (tenant.active || accessScope === IAccessScope.system) {
          return (
              <Link to={RouterConfig.accounts.details(tenant.id)}>
                {tenant.name}
              </Link>
          )
        } else {
          return (<>{tenant.name}</>)
        }
      }
    },
    {
      key: 'active',
      title: translate('common.status') as string,
      renderFilter: function BasicStringFilter() {
        return (
            <SelectFilter<ITenant, string>
                items={[
                  {
                    value: 'true',
                    label: 'active',
                    func: 'eq'
                  },
                  {
                    value: 'false',
                    label: 'inactive',
                    func: 'eq'
                  }
                ]}
                columnKey='active'
                label={translate('common.status') as string}
                minSearchLength={1}
            />
        );
      },
      render: tenant => (
          <Tag type={tenant.active ? TagType.ACTIVE : TagType.INACTIVE}>
            <Translate id={`common.statuses.${getEntityStatus(tenant.active)}`} />
          </Tag>
      ),
    },
    {
      key: 'friendly_identifier',
      title: translate('accounts.table.accountId') as string,
      renderFilter: function BasicStringFilter() {
        return (
            <FreeTextFilter<ITenant>
                label='ID'
                columnKey='friendly_identifier'
            />
        );
      }
    },
    {
      key: 'from',
      title: translate('accounts.table.startDate') as string,
      render: tenant => (
          <>
            {formatDate(tenant.service_period.from, profile?.date_format as string)}
          </>
      ),
    },
    {
      key: 'applications',
      title: translate('manageUser.administrators.table.enabledServices') as string,
      sorter: false,
      render: account => renderAppNames(account),
      renderFilter: function BasicStringFilter() {
        return (
            <SelectFilter<ITenant, string>
                items={applicationOptions()}
                columnKey='applications'
                label={translate('manageUser.administrators.table.enabledServices') as string}
                minSearchLength={1}
            />
        );
      },
    }
  ]

  const onEditTenant = (id: string): void => {
    setAccount(tenants?.find(tenant => tenant.id === id))
    setShowEditSideRail(true)
  }

  const onTableChange = (newMeta:TableMetaProps<ITenant>): void => {
    let newSortedInfo = {
      columnKey: newMeta.sort?.columnKey,
      order: newMeta.sort?.order
    }

    if (newSortedInfo.columnKey === undefined && newSortedInfo.order === undefined) {
      newSortedInfo = {
        columnKey: 'name',
        order: 'ascend'
      }
    }

    if (!isEqual(newSortedInfo, sortedInfo)) {
      // if sort go back to page 1
      setCurrent(newMeta.currentPage = 1)
      setSortedInfo({
        columnKey: newMeta.sort?.columnKey,
        order: newMeta.sort?.order
      })
    }

    if (!isEqual(newMeta.currentPage, current)) {
      setCurrent(newMeta.currentPage)
    }

    if(!isEqual(newMeta.filters, meta.filters)) {
      // if filter go back to page 1
      setCurrent(newMeta.currentPage = 1)

      // formats array into structure compatible with onFilter
      parseFilter(newMeta)
    }
    setMeta(newMeta)
    fetchTenants(newMeta)
  }

  const initiateCreateAccount = () => {
    setShowSideRail(true)
  }

  const onEditServices = (id: string): void => {
    history.push(RouterConfig.accounts.services(id))
  }
  const onEditSigninDetails = (id: string): void => {
    history.push(RouterConfig.accounts.identityAndAccess(id))
  }

  const getRowActions = () => {
    const canManageAccount = permissions.canManageAccount && accessScope === IAccessScope.system
    return [
      {
        title: translate('accounts.editAccount') as string,
        isVisible: canManageAccount,
        onClick: onEditTenant,
        icon: <FontAwesomeIcon icon={faPencil as IconProp}/>
      },
      {
        title: translate('accounts.editAccountServices') as string,
        isVisible: canManageAccount,
        onClick: onEditServices,
        icon: <FontAwesomeIcon icon={faPencil as IconProp}/>
      },
      {
        title: translate('accounts.editSignInDetails') as string,
        isVisible: canManageAccount,
        onClick: onEditSigninDetails,
        icon: <FontAwesomeIcon icon={faPencil as IconProp}/>
      },
    ]
  }

  const updateNav = () => {
    fetchTenants(meta)
  }

  const canCreateCustomer =
      permissions.canManageAccount &&
      permissions.canManagePermission &&
      accessScope === IAccessScope.system

  return (
      <section className={styles.tenants}>
        <SecondaryNav
            className={styles.secondaryNav}
            items={[
              {
                id: 'accounts',
                label: <Translate id='common.form.accounts'/>,
                collapsed: false,
                subItems: [
                  {
                    id: 'all-accounts',
                    label: <Translate id='common.form.allAccounts'/>,
                    url: RouterConfig.urlMap.accounts,
                    active: window.location.pathname === RouterConfig.urlMap.accounts,
                    externalLink: false,
                    disabled: false,
                  }
                ]
              }
            ]}
        />

        <div className={styles.content}>
          <header className={styles.header}>
            <h1><Translate id='common.form.allAccounts'/></h1>
            <Button size='small' hidden={!canCreateCustomer} onClick={initiateCreateAccount}>
              <Translate id='businessUnits.wizardCreateBtn'/>
            </Button>
          </header>

          {showPageLevelError && showErrorBannerAfterDismiss &&
              <div className={styles.actions}>
                <Alert
                    bannerType={AlertTypes.ERROR}
                    description={<a onClick={() => populateTable(30)}>{errorMessage}</a>}
                    showDismissIcon
                    onDismiss={onDismissErrorBanner}
                />
              </div>
          }

          {showPageLevelWarning && showWarnBannerAfterDismiss &&
              <div className={styles.actions}>
                <Alert
                    bannerType={AlertTypes.WARNING}
                    description={<a onClick={() => populateTable(60)}>{warningMessage}</a>}
                    showDismissIcon
                    onDismiss={onDismissWarnBanner}
                />
              </div>
          }

          <Table
              className={styles.table + ' ' + 'resizable-table-body'}
              columns={getColumns()}
              items={tenants}
              getRowKey={tenant => tenant.id}
              getActions={() => getRowActions()}
              actionsButtonsQuantity={1}
              meta={meta}
              onChange={onTableChange}
              selectedRowKeys={tenantIds}
              onRowSelect={onSelectionChange}
              disableCheckBoxes
              saveDataName={'accounts-table-' + profile?.id}
              loading={loading}
          />
        </div>

        <SideRail
            className={cn(styles.createAccountSideRail, showSideRail && styles.expanded)}
            headline={<h2><Translate id='businessUnits.wizardCreateBtn'/></h2>}
            show={showSideRail}
            onDismiss={() => setShowSideRail(!showSideRail)}
            lightbox
        >
          <div className={styles.createAccountContent}>
            <CreateAccount
                services={applications}
                localeList={localeList}
                onCancel={() => {setShowSideRail(false)}}
            />
          </div>
        </SideRail>
      <SideRail
        className={cn(styles.editAccountSideRail, showEditSideRail && styles.expanded)}
        headline={<h2><Translate id='accounts.details.updateAccountDetails' /></h2>}
        show={showEditSideRail}
        onDismiss={() => setShowEditSideRail(false)}
        lightbox
      >
        <EditOverView
          account={account}
          localeList={localeList}
          onCancel={() => { setShowEditSideRail(false) }}
          accessScope={accessScope}
          updateNav={updateNav}
          setAccount={setAccount}
        />
      </SideRail>
      </section>
  )

}

export default connect(
    (state: IAppState): IStateProps => {
      return {
        tenants: state.tenants.items, // all accounts
        isLoading: state.tenants.isLoading,
        accessScope: state.profile.data?.access_scope,
        profileTenants: state.profile.data?.accounts, // accounts logged in user has access too
        profile: state.profile.data,
        localeList: state.locales
      }
    },
    {fetchTenants, deleteTenants, fetchProfile, fetchExpiringAccounts, getApplications}
)(withPermissionsHOC(TenantsPage, ['canViewAccount']))
